import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Table } from "react-bootstrap";
import { hasPermission } from "../../../Utils/permissions";
import api from "../../../Utils/api";

const EMPTY_ROLE = { name: "", description: "", permissions: [], active: true };

function RolesPermissoes() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [draft, setDraft] = useState(EMPTY_ROLE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const permissionGroups = useMemo(() => permissions.reduce((groups, permission) => {
    groups[permission.module] = [...(groups[permission.module] || []), permission];
    return groups;
  }, {}), [permissions]);

  const load = async () => {
    setError("");
    setLoading(true);
    try {
      const roleResponse = await api.get("/access/roles");
      const usersResponse = hasPermission("users.view") || hasPermission("users.manage")
        ? await api.get("/user")
        : { data: [] };
      setRoles(roleResponse.data.roles || []);
      setPermissions(roleResponse.data.permissions || []);
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível carregar roles e usuários.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setSelectedRole(null);
    setDraft(EMPTY_ROLE);
    setNotice("");
    setError("");
  };

  const startEdit = (role) => {
    setSelectedRole(role);
    setDraft({
      name: role.name,
      description: role.description || "",
      permissions: role.permissions || [],
      active: role.active,
    });
    setNotice("");
    setError("");
  };

  const togglePermission = (permissionId, checked) => {
    setDraft((current) => ({
      ...current,
      permissions: checked
        ? [...new Set([...current.permissions, permissionId])]
        : current.permissions.filter((item) => item !== permissionId),
    }));
  };

  const toggleModule = (modulePermissions, checked) => {
    const moduleIds = modulePermissions.map((permission) => permission.id);
    setDraft((current) => ({
      ...current,
      permissions: checked
        ? [...new Set([...current.permissions, ...moduleIds])]
        : current.permissions.filter((permission) => !moduleIds.includes(permission)),
    }));
  };

  const saveRole = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    try {
      const payload = {
        name: draft.name,
        description: draft.description,
        permissions: draft.permissions,
        active: draft.active,
      };
      if (selectedRole) {
        await api.put(`/access/roles/${selectedRole._id}`, payload);
      } else {
        await api.post("/access/roles", payload);
      }
      setNotice("Role salva.");
      await load();
      startCreate();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível salvar a role.");
    }
  };

  const deactivateRole = async (role) => {
    setError("");
    setNotice("");
    try {
      await api.put(`/access/roles/${role._id}`, { active: false });
    } catch (requestError) {
      const data = requestError.response?.data;
      if (!data?.requiresConfirmation || !window.confirm(data.message)) {
        setError(data?.message || "Não foi possível desativar a role.");
        return;
      }
      try {
        await api.put(`/access/roles/${role._id}`, { active: false, confirmReassign: true });
      } catch (confirmError) {
        setError(confirmError.response?.data?.message || "Não foi possível desativar a role.");
        return;
      }
    }
    setNotice(`Role ${role.name} desativada.`);
    await load();
  };

  const changeUserRole = async (user, roleKey) => {
    setError("");
    setNotice("");
    try {
      await api.patch(`/access/users/${user._id}/role`, { roleKey });
      setNotice(`Role de ${user.userName} atualizada.`);
      await refreshOwnPermissions(user._id);
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível alterar a role do usuário.");
    }
  };

  const toggleUserStatus = async (user) => {
    setError("");
    setNotice("");
    const active = !(user.isAtivo === true || user.isAtivo === "true");
    try {
      await api.patch(`/access/users/${user._id}/status`, { active });
      setNotice(`Conta de ${user.userName} ${active ? "ativada" : "desativada"}.`);
      if (!active && user._id === localStorage.getItem("x-user-id")) {
        localStorage.removeItem("x-permissions");
      }
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível alterar o status da conta.");
    }
  };

  const effectivePermissions = (roleKey) =>
    roles.find((role) => role.key === roleKey)?.permissions || [];

  const refreshOwnPermissions = async (userId) => {
    if (userId !== localStorage.getItem("x-user-id")) return;
    try {
      const { data } = await api.get("/auth/session");
      localStorage.setItem("x-role-key", data.user.roleKey || "");
      localStorage.setItem("x-permissions", JSON.stringify(data.user.permissions || []));
    } catch (requestError) {
      localStorage.removeItem("x-permissions");
      setError(requestError.response?.data?.message || "A sessão foi alterada; atualize a página para recarregar o acesso.");
    }
  };

  if (!hasPermission("roles.view")) {
    return <Alert variant="warning">Sua conta não possui permissão para consultar roles.</Alert>;
  }

  return (
    <div className="bo-section mt-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-1">Roles e Permissões</h3>
          <p className="text-muted mb-0">Acesso efetivo é validado no servidor em cada requisição.</p>
        </div>
        {hasPermission("roles.manage") && (
          <Button variant="primary" onClick={startCreate}>Criar role</Button>
        )}
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}
      {loading && <p role="status">Carregando roles e usuários...</p>}

      {!loading && (
        <Table responsive bordered size="sm">
          <thead>
            <tr><th>Role</th><th>Descrição</th><th>Usuários</th><th>Status</th><th>Atualizada</th><th>Ações</th></tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role._id}>
                <td>{role.name}</td>
                <td>{role.description}</td>
                <td>{role.userCount || 0}</td>
                <td>{role.active ? "Ativa" : "Inativa"}</td>
                <td>{role.updatedAt ? new Date(role.updatedAt).toLocaleDateString("pt-BR") : "—"}</td>
                <td className="d-flex gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => startEdit(role)}>
                    {hasPermission("roles.manage") ? "Editar" : "Visualizar"}
                  </Button>
                  {hasPermission("roles.manage") && role.active &&
                    !["administrador", "basico"].includes(role.key) && (
                    <Button size="sm" variant="outline-danger" onClick={() => deactivateRole(role)}>Desativar</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {selectedRole && !hasPermission("roles.manage") && (
        <section className="mt-3" aria-label={`Permissões da role ${selectedRole.name}`}>
          <h4>Permissões de {selectedRole.name}</h4>
          <ul>
            {(selectedRole.permissions || []).map((permission) => (
              <li key={permission}>{permissions.find((item) => item.id === permission)?.label || permission}</li>
            ))}
            {!selectedRole.permissions?.length && <li>Nenhuma permissão configurada.</li>}
          </ul>
        </section>
      )}

      {hasPermission("roles.manage") && (
        <Form className="mt-4" onSubmit={saveRole}>
          <h4>{selectedRole ? `Editar role: ${selectedRole.name}` : "Criar role"}</h4>
          <Row className="g-3">
            <Col xs={12} md={5}>
              <Form.Label htmlFor="role-name">Nome</Form.Label>
              <Form.Control id="role-name" required minLength={2} maxLength={80}
                value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </Col>
            <Col xs={12} md={7}>
              <Form.Label htmlFor="role-description">Descrição</Form.Label>
              <Form.Control id="role-description" maxLength={500}
                value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
            </Col>
          </Row>
          <Form.Check className="mt-3" type="switch" id="role-active" label="Role ativa"
            checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} />
          {Object.entries(permissionGroups).map(([module, modulePermissions]) => (
            <fieldset className="border rounded p-3 mt-3" key={module}>
              <legend className="float-none w-auto px-2 fs-6">{module}</legend>
              <Form.Check className="fw-bold mb-2" type="checkbox"
                id={`all-${module}`} label="Selecionar módulo"
                checked={modulePermissions.every((permission) => draft.permissions.includes(permission.id))}
                onChange={(event) => toggleModule(modulePermissions, event.target.checked)} />
              <Row>
                {modulePermissions.map((permission) => (
                  <Col xs={12} md={6} key={permission.id}>
                    <Form.Check type="checkbox" id={permission.id} label={permission.label}
                      checked={draft.permissions.includes(permission.id)}
                      onChange={(event) => togglePermission(permission.id, event.target.checked)} />
                  </Col>
                ))}
              </Row>
            </fieldset>
          ))}
          <div className="d-flex gap-2 mt-3">
            <Button type="submit" variant="success">Salvar</Button>
            <Button type="button" variant="outline-secondary" onClick={startCreate}>Cancelar</Button>
          </div>
        </Form>
      )}

      {(hasPermission("users.view") || hasPermission("users.manage")) && (
        <section className="mt-5">
          <h4>Usuários e permissões efetivas</h4>
          <Table responsive bordered size="sm">
            <thead><tr><th>Usuário</th><th>Role</th><th>Permissões efetivas</th><th>Status</th><th>Ações</th></tr></thead>
            <tbody>
              {users.map((user) => {
                const currentRole = roles.some((role) => role.key === user.roleKey && role.active)
                  ? user.roleKey
                  : "";
                const active = user.isAtivo !== false && user.isAtivo !== "false";
                return (
                  <tr key={user._id}>
                    <td>{user.userName}<br /><small>{user.userEmail}</small></td>
                    <td>
                      {hasPermission("users.manage") ? (
                        <Form.Select aria-label={`Role de ${user.userName}`} value={currentRole}
                          onChange={(event) => changeUserRole(user, event.target.value)}>
                          {!currentRole && <option value="" disabled>Sem role válida</option>}
                          {roles.filter((role) => role.active).map((role) => (
                            <option key={role.key} value={role.key}>{role.name}</option>
                          ))}
                        </Form.Select>
                      ) : roles.find((role) => role.key === currentRole)?.name || "Sem role válida"}
                    </td>
                    <td>{effectivePermissions(currentRole).join(", ") || "Nenhuma"}</td>
                    <td>{active
                      ? "Ativa"
                      : user.emailConfirmed === false
                        ? "Aguardando confirmação de e-mail"
                        : user.accessPending
                          ? "Aguardando liberação administrativa"
                          : "Inativa"}</td>
                    <td>{hasPermission("users.manage") && <Button size="sm" variant={active ? "outline-danger" : "outline-success"}
                      disabled={!active && user.emailConfirmed === false}
                      title={!active && user.emailConfirmed === false ? "O usuário precisa confirmar o e-mail primeiro." : undefined}
                      onClick={() => toggleUserStatus(user)}>{active ? "Desativar" : user.accessPending ? "Liberar acesso" : "Ativar"}</Button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </section>
      )}
    </div>
  );
}

export default RolesPermissoes;
